import React, { useCallback, useState, useEffect } from 'react';
import { TextField, Autocomplete } from '@mui/material';
import './autocomplete.module.css';
import { Procedure } from 'types/procedure';
import { toast } from 'react-toastify';
import { api } from 'services/api';
import ProcedureItem from './ProcedureItem';

let timer: NodeJS.Timeout;

interface AutocompleteFromProps {
  onSelect(value: Procedure | null): void;
  label: string;
  placeholder: string;
  selected: Procedure | null;
  helperText?: string;
  hideHint?: boolean;
  popupIcon?: React.ReactNode;
  error?: boolean;
  disabled?: boolean;
  specialtyId?: number | null;
  ref?: React.Ref<HTMLInputElement>;
  type?: 'G' | 'P';
}

const ProceduresAutoComplete: React.FC<AutocompleteFromProps> = ({
  onSelect,
  label,
  type = '',
  placeholder,
  selected,
  helperText = '',
  hideHint = false,
  popupIcon,
  error = false,
  disabled = false,
  specialtyId,
  ref,
}) => {
  const [loading, setLoading] = useState(false);
  const [filtered, setFiltered] = useState<Procedure[]>([]);
  const [options, setOptions] = useState<Procedure[]>([]);

  const handleSelect = useCallback(
    (option: Procedure) => {
      onSelect(option);
      setFiltered([]);
    },
    [onSelect],
  );

  useEffect(() => {
    setFiltered(options);
  }, [options]);

  const findProcedures = useCallback(
    async (term: string) => {
      setLoading(true);
      api
        .get('/procedures/all', { params: { name: term, active: 1, specialty_id: specialtyId || '', type } })
        .then(response => {
          setOptions(response.data);
        })
        .catch(err => {
          toast.error('Ocorreu um erro ao buscar os procedimentos');
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [specialtyId],
  );

  function handleChange(value: string) {
    clearTimeout(timer);

    if (value.length < 2) {
      return;
    }

    timer = setTimeout(() => findProcedures(value), 400);
  }

  return (
    <Autocomplete
      fullWidth
      autoHighlight
      handleHomeEndKeys
      openOnFocus
      {...(popupIcon ? { popupIcon } : {})}
      disabled={disabled}
      value={selected}
      options={filtered}
      noOptionsText="Nenhum procedimento encontrado"
      getOptionLabel={option => option.name}
      onChange={(event, newValue) => onSelect(newValue)}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <ProcedureItem handleSelect={handleSelect} option={option} selected={selected} />
        </li>
      )}
      renderInput={params => (
        <TextField
          {...params}
          inputRef={ref}
          disabled={disabled}
          onBlur={() => setFiltered([])}
          onChange={e => handleChange(e.target.value)}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText || (hideHint ? undefined : 'Digite 2 ou mais caracteres')}
          fullWidth
        />
      )}
    />
  );
};

export default ProceduresAutoComplete;
